import React, { useState, useEffect } from "react";
import { CloseOutlined, PlayCircleOutlined, QrcodeOutlined } from "@ant-design/icons";
import { Modal } from "@mui/material";
import MainBtn from "../main/main_btn/MainBtn";
import { App, Input, Steps, Alert, Button } from "antd";
import { connect } from "react-redux";
import { mapDispatchToProps, mapStateToProps } from "../../store/dispatcher";
import { DispatchProps, StateProps } from "../../store";
import { useTranslation } from "react-i18next";
import axios from "../../services/axios";
import { runes } from 'runes2';
import QRScannerComponent from "../common/QRScanner";
import DeviceConfigurationEmbed from "../common/DeviceConfigurationEmbed";
import ParameterConfigurationPicker from "../device/ParameterConfigurationPicker";
import {
  ParameterConfigurationOption,
  getDefaultConfigId,
} from "../../lib/parameterConfiguration";
import { USER_DEVICE_SETUP_VIDEO_URL } from "../../constants/constants";
import { Home } from "../../lib/Home";
import { Device } from "../../lib/Device";
import "./OnboardingWizard.scss";

const empty_schedule = [
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
];

const OnboardingWizard = ({
  open = false,
  close = () => null,
  createDevice,
  updateDevice,
  fetchHomes,
}: {
  open: boolean;
  close: () => void;
  createDevice?: any;
  updateDevice?: any;
  fetchHomes?: any;
} & StateProps & DispatchProps) => {
  const { t } = useTranslation(["home", "wizard"]);
  const { notification } = App.useApp();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [qrScannerOpen, setQrScannerOpen] = useState(false);
  
  const [userFormData, setUserFormData] = useState({
    email: "",
    first_name: "",
    last_name: "",
  });
  const [createdUser, setCreatedUser] = useState<any>(null);
  const [createdHome, setCreatedHome] = useState<Home | null>(null);
  
  const [addDevice, setAddDevice] = useState<boolean | null>(null);
  
  const [deviceFormData, setDeviceFormData] = useState({
    device_id: "",
    name: "",
  });
  const [createdDevice, setCreatedDevice] = useState<Device | null>(null);
  const [parameterConfigs, setParameterConfigs] = useState<ParameterConfigurationOption[]>([]);
  const [selectedConfigId, setSelectedConfigId] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setUserFormData({ email: "", first_name: "", last_name: "" });
      setCreatedUser(null);
      setCreatedHome(null);
      setAddDevice(null);
      setDeviceFormData({ device_id: "", name: "" });
      setCreatedDevice(null);
      setParameterConfigs([]);
      setSelectedConfigId(null);
    }
  }, [open]);

  const handleQRScan = (data: { device_id: string; secret: string }) => {
    setDeviceFormData(prev => ({
      ...prev,
      device_id: data.device_id,
      name: data.device_id,
    }));
    setQrScannerOpen(false);
  };

  const createUser = async () => {
    if (!userFormData.email || !userFormData.first_name || !userFormData.last_name) {
      notification.error({
        message: t("wizard:error_fill_all_fields"),
        placement: "topLeft",
      });
      return;
    }

    setLoading(true);
    try {
      const defaultPassword = "Soliseco1234!";
      const response = await axios.post("/api-v1/users/create-user/", {
        email: userFormData.email,
        first_name: userFormData.first_name,
        last_name: userFormData.last_name,
        password: defaultPassword,
      });

      setCreatedUser(response.data.user);
      setCreatedHome(response.data.home);
      
      if (fetchHomes) {
        await fetchHomes();
      }
      
      notification.success({
        message: t("wizard:success_user_created"),
        description: t("wizard:success_home_created_auto"),
        placement: "topLeft",
      });

      setCurrentStep(1);
    } catch (error: any) {
      const errorMsg = error.response?.data?.username?.[0] || 
                       error.response?.data?.email?.[0] || 
                       error.response?.data?.error || 
                       t("wizard:error_failed_create_user");
      notification.error({
        message: t("wizard:error_create_user"),
        description: errorMsg,
        placement: "topLeft",
      });
    }
    setLoading(false);
  };

  const createDeviceHandler = async () => {
    if (!deviceFormData.device_id || !deviceFormData.name) {
      notification.error({
        message: t("wizard:error_fill_device_fields"),
        placement: "topLeft",
      });
      return;
    }

    if (!createdHome) {
      notification.error({
        message: t("wizard:error_no_home"),
        placement: "topLeft",
      });
      return;
    }

    setLoading(true);
    try {
      const requestData: any = {
        name: deviceFormData.name,
        home: createdHome.id,
        device_id: deviceFormData.device_id,
        soliseco_model: "1P_Intraheat",
        schedule: [...empty_schedule],
      };

      const response = await axios.post(`/api-v1/device/`, requestData);
      const device = response.data as Device;
      setCreatedDevice(device);
      if (createDevice) {
        createDevice(device);
      }
      if (device.soliseco_model) {
        try {
          const configsRes = await axios.get(
            `/api-v1/config/parameter-configurations/?soliseco_model=${encodeURIComponent(device.soliseco_model)}`
          );
          const configs = configsRes.data as ParameterConfigurationOption[];
          setParameterConfigs(configs);
          setSelectedConfigId(
            getDefaultConfigId(configs, device.soliseco_model, device.parameter_configuration?.id)
          );
        } catch {
          setParameterConfigs([]);
        }
      }

      notification.success({
        message: t("wizard:success_device_created"),
        placement: "topLeft",
      });

      setCurrentStep(3);
    } catch (error: any) {
      const errorMsg = error.response?.data?.device_id?.[0] || 
                       error.response?.data?.error || 
                       t("wizard:error_failed_create_device");
      notification.error({
        message: t("wizard:error_create_device"),
        description: errorMsg,
        placement: "topLeft",
      });
    }
    setLoading(false);
  };

  const handleAddDevice = () => {
    setAddDevice(true);
    setCurrentStep(2);
  };

  const handleSkip = () => {
    setAddDevice(false);
    setCurrentStep(5);
  };

  const saveSelectedConfiguration = async () => {
    if (!createdDevice || selectedConfigId == null) {
      setCurrentStep(5);
      return;
    }
    setLoading(true);
    try {
      const response = await axios.patch(`/api-v1/device/${createdDevice.id}/`, {
        parameter_configuration: selectedConfigId,
        apply_default_values: true,
      });
      if (updateDevice) {
        updateDevice({
          id: createdDevice.id,
          data: response.data,
          updated: true,
        });
      }
      setCurrentStep(5);
    } catch (error: any) {
      notification.error({
        message: t("wizard:error_save_config"),
        description:
          error.response?.data?.parameter_configuration?.[0] ||
          error.response?.data?.parameter_configuration ||
          t("wizard:error_save_config_description"),
        placement: "topLeft",
      });
    }
    setLoading(false);
  };

  const handleNext = () => {
    if (currentStep === 0) {
      createUser();
    } else if (currentStep === 2) {
      createDeviceHandler();
    } else if (currentStep === 3) {
      setCurrentStep(4);
    } else if (currentStep === 4) {
      saveSelectedConfiguration();
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    close();
    if (fetchHomes) {
      fetchHomes();
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return (
          <div>
            <Alert
              message={t("wizard:step1_title")}
              description={t("wizard:step1_description")}
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />
            <form className="classic-form">
              <div className="input-block">
                <label htmlFor="user-email-input">{t("wizard:email_label")}</label>
                <Input
                  size="small"
                  value={userFormData.email}
                  onChange={(e) =>
                    setUserFormData({
                      ...userFormData,
                      email: e.target.value,
                    })
                  }
                  placeholder={t("wizard:email_placeholder")}
                  type="email"
                  id="user-email-input"
                />
              </div>
              <div className="input-block">
                <label htmlFor="user-first-name-input">{t("wizard:first_name_label")}</label>
                <Input
                  size="small"
                  value={userFormData.first_name}
                  onChange={(e) =>
                    setUserFormData({
                      ...userFormData,
                      first_name: e.target.value,
                    })
                  }
                  placeholder={t("wizard:first_name_placeholder")}
                  type="text"
                  id="user-first-name-input"
                />
              </div>
              <div className="input-block">
                <label htmlFor="user-last-name-input">{t("wizard:last_name_label")}</label>
                <Input
                  size="small"
                  value={userFormData.last_name}
                  onChange={(e) =>
                    setUserFormData({
                      ...userFormData,
                      last_name: e.target.value,
                    })
                  }
                  placeholder={t("wizard:last_name_placeholder")}
                  type="text"
                  id="user-last-name-input"
                />
              </div>
            </form>
            {createdUser && createdHome && (
              <Alert
                message={t("wizard:success_user_created")}
                description={t("wizard:success_home_created_auto")}
                type="success"
                showIcon
                style={{ marginTop: '16px' }}
              />
            )}
          </div>
        );

      case 1:
        return (
          <div>
            <Alert
              message={t("wizard:step2_title")}
              description={t("wizard:step2_description")}
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />
            {createdHome && (
              <div style={{ 
                padding: '16px', 
                backgroundColor: '#f0f0f0', 
                borderRadius: '8px',
                marginBottom: '24px'
              }}>
                <strong>{t("wizard:created_home_label")}:</strong> {createdHome.name}
              </div>
            )}
          </div>
        );

      case 2:
        return (
          <div>
            <Alert
              message={t("wizard:step3_title")}
              description={t("wizard:step3_description")}
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />
            <form className="classic-form">
              <div className="input-block">
                <label htmlFor="device-id-input">{t("wizard:device_id_label")}</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <Input
                    size="small"
                    value={deviceFormData.device_id}
                    onChange={(e) =>
                      setDeviceFormData({
                        ...deviceFormData,
                        device_id: e.target.value,
                        name: e.target.value || deviceFormData.name,
                      })
                    }
                    placeholder={t("wizard:device_id_placeholder")}
                    type="text"
                    id="device-id-input"
                    style={{ flex: 1 }}
                  />
                  <Button
                    icon={<QrcodeOutlined />}
                    onClick={() => setQrScannerOpen(true)}
                  >
                    {t("wizard:scan_qr")}
                  </Button>
                </div>
              </div>
              <div className="input-block">
                <label htmlFor="device-name-input">{t("wizard:device_name_label")}</label>
                <Input
                  size="small"
                  value={deviceFormData.name}
                  onChange={(e) =>
                    setDeviceFormData({
                      ...deviceFormData,
                      name: e.target.value,
                    })
                  }
                  placeholder={t("wizard:device_name_placeholder")}
                  type="text"
                  id="device-name-input"
                  count={{
                    show: true,
                    max: 25,
                    strategy: (txt) => runes(txt).length,
                    exceedFormatter: (txt, { max }) => runes(txt).slice(0, max).join(''),
                  }}
                />
              </div>
            </form>
          </div>
        );

      case 3:
        return (
          <div>
            <Alert
              message={t("wizard:step4_title")}
              description={t("wizard:step4_description")}
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />
            <div style={{ 
              padding: '20px', 
              backgroundColor: '#f5f5f5', 
              borderRadius: '8px',
              border: '1px solid #d9d9d9',
              marginBottom: '20px'
            }}>
              <h4 style={{ marginTop: 0, marginBottom: '12px' }}>{t("wizard:local_page_steps_title")}</h4>
              <ol style={{ margin: 0, paddingLeft: '20px' }}>
                <li style={{ marginBottom: '8px' }}>{t("wizard:local_page_step_1")}</li>
                <li style={{ marginBottom: '8px' }}>{t("wizard:local_page_step_2")}</li>
                <li style={{ marginBottom: '8px' }}>{t("wizard:local_page_step_3")}</li>
                <li>{t("wizard:local_page_step_4")}</li>
              </ol>
            </div>
            <DeviceConfigurationEmbed
              fallbackTitle={t("wizard:local_page_step_3")}
            />
          </div>
        );

      case 4:
        return (
          <div>
            <Alert
              message={t("wizard:step_config_title")}
              description={t("wizard:step_config_description")}
              type="info"
              showIcon
              style={{ marginBottom: '24px' }}
            />
            <ParameterConfigurationPicker
              configs={parameterConfigs}
              solisecoModel={createdDevice?.soliseco_model}
              currentConfigId={createdDevice?.parameter_configuration?.id}
              value={selectedConfigId}
              onChange={setSelectedConfigId}
            />
          </div>
        );

      case 5:
        return (
          <div>
            <Alert
              message={t("wizard:step5_title")}
              description={t("wizard:step5_description")}
              type="success"
              showIcon
              style={{ marginBottom: '24px' }}
            />
          </div>
        );

      default:
        return null;
    }
  };

  const steps = [
    {
      title: t("wizard:step1_title"),
      description: t("wizard:step1_description_short"),
    },
    {
      title: t("wizard:step2_title"),
      description: t("wizard:step2_description_short"),
    },
    {
      title: t("wizard:step3_title"),
      description: t("wizard:step3_description_short"),
    },
    {
      title: t("wizard:step4_title"),
      description: t("wizard:step4_description_short"),
    },
    {
      title: t("wizard:step_config_title"),
      description: t("wizard:step_config_description_short"),
    },
    {
      title: t("wizard:step5_title"),
      description: t("wizard:step5_description_short"),
    },
  ];

  const adjustedSteps = addDevice === false && currentStep >= 2 
    ? steps.filter((_, index) => index !== 2 && index !== 3 && index !== 4)
    : steps;

  const getCurrentStepIndex = () => {
    if (addDevice === false && currentStep >= 2) {
      return currentStep === 5 ? 2 : currentStep;
    }
    return currentStep;
  };

  return (
    <>
      <Modal className="soliseco-modal" open={open} onClose={close}>
        <div className="modal-center">
          <div className="modal-header">
            <div className="close-container" onClick={close}>
              <CloseOutlined />
            </div>
            <span className="title">{t("wizard:wizard_title")}</span>
            <p>{t("wizard:wizard_description")}</p>
          </div>
          <div className="modal-content">
            <div className="video-tutorial-container">
              <div className="video-tutorial-content">
                <div className="video-tutorial-text">
                  <h4>{t("wizard:video_tutorial_title")}</h4>
                  <p>{t("wizard:video_tutorial_description")}</p>
                </div>
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  size="middle"
                  onClick={() => {
                    window.open(USER_DEVICE_SETUP_VIDEO_URL, '_blank');
                  }}
                  className="video-tutorial-button"
                >
                  {t("wizard:watch_video_btn")}
                </Button>
              </div>
            </div>
            <Steps 
              current={getCurrentStepIndex()} 
              items={adjustedSteps} 
              style={{ marginBottom: '24px' }} 
            />
            {renderStepContent()}
          </div>
          <div className="modal-footer">
            {currentStep > 0 && currentStep < 5 && (
              <MainBtn
                title={t("wizard:back_btn")}
                color="secondary"
                onClick={handleBack}
                disabled={loading}
              />
            )}
            {currentStep === 1 ? (
              <div className="step2-buttons-container">
                <MainBtn
                  title={t("wizard:skip_btn")}
                  color="secondary"
                  onClick={handleSkip}
                  disabled={loading}
                />
                <MainBtn
                  title={t("wizard:add_device_btn")}
                  color="primary"
                  onClick={handleAddDevice}
                  disabled={loading}
                />
              </div>
            ) : currentStep < 5 ? (
              <MainBtn
                title={t("wizard:next_btn")}
                loading={loading}
                onClick={handleNext}
                disabled={
                  (currentStep === 0 && (!userFormData.email || !userFormData.first_name || !userFormData.last_name)) ||
                  (currentStep === 2 && (!deviceFormData.device_id || !deviceFormData.name)) ||
                  (currentStep === 4 && selectedConfigId == null)
                }
              />
            ) : (
              <MainBtn
                title={t("wizard:finish_btn")}
                onClick={handleFinish}
              />
            )}
            {currentStep === 0 && (
              <MainBtn title={t("main:cancel")} color="secondary" onClick={close} />
            )}
          </div>
        </div>
      </Modal>
      
      <QRScannerComponent
        open={qrScannerOpen}
        onClose={() => setQrScannerOpen(false)}
        onScan={handleQRScan}
      />
    </>
  );
};

export default connect<StateProps, DispatchProps>(
  mapStateToProps,
  mapDispatchToProps
)(OnboardingWizard);
